import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setReportLoading } from '../../redux/actions/report'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import {
  Drawer,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  FormControl,
  Autocomplete,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import {
  CollapsedField,
  CustomInputLabel,
  CustomTextField,
} from '../../components/restyled-mui/CustomInputs'
import moment from 'moment'
// import DateRangePicker from '../../components/forms/DateRangePicker/DateRangePicker'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { calendarIcon } from '../../assets/fontawesome/calendarIcon'
import { PrimaryButton } from '../../components/restyled-mui/CustomButton'
import Report from '../../api/report'

const sitesListWidth = 350
const tabletSidebarWidth = 48

const useStyles = makeStyles(theme => ({
  childrenContainer: {
    marginLeft: props =>
      props.fullscreen || props.toggleable ? 0 : sitesListWidth,
    width: props =>
      props.fullscreen || props.toggleable
        ? '100%'
        : `calc(100% - ${sitesListWidth}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  formContainer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    padding: '15px 25px',
    width: props =>
      props.fullscreen
        ? '100%'
        : `calc(${sitesListWidth} - ${theme.spacing(4)}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
  customSubtitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    // marginBottom: '10px',
  },
  customSubtitleDate: {
    fontSize: 14,
    color: theme.palette.primary.main,
    marginBottom: '10px',
  },
  drawer: {
    marginLeft: props => (props.toggleable ? tabletSidebarWidth - 1 : 0),
    borderRight: `${theme.palette.menu.border} 1px solid`,
    width: props => (props.fullscreen ? '100%' : sitesListWidth),
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    width: tabletSidebarWidth,
    zIndex: 1400,
    backgroundColor: theme.palette.menu.bg,
    borderRight: `${theme.palette.menu.border} 1px solid`,
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 3),
  },
  menuItemIcon: {
    textAlign: 'center',
    minWidth: '100%',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  endAdornmentIcon: {
    height: 21,
    width: 21,
    color: theme.palette.generalUi.icons,
  },
  calendarField: {
    '& .MuiOutlinedInput-input': {
      fontSize: 14,
    },
    '& .MuiInputLabel-root': {
      fontSize: 14,
    },
    '& .MuiInputAdornment-positionEnd': {
      marginRight: 4,
    },
    '& .MuiIconButton-root': {
      padding: 6,
    },
  },
  sectionDelimiter: {
    paddingTop: '20px',
  },
  runButtonDelimiter: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(2),
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.15 },
  },
  generatingText: {
    animation: '$blink 1.2s ease-in-out infinite',
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 600,
    paddingTop: theme.spacing(1),
  },
  menuIcon: {
    color: theme.palette.menu.icons,
  },
  selectedMenuIcon: {
    color: theme.palette.primary.main,
  },
  tickIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
}))

const PortfolioOverviewPanel = props => {
  const classes = useStyles(props)
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const reportLoading = useSelector(state => state.report.reportLoading)
  const [selectedDate, setSelectedDate] = useState(moment().toDate())
  const [dateError, setDateError] = useState(false)
  const {
    container,
    userSetups,
    setSetup,
    selectedSetup,
    loading,
    pageError,
    isAdmin,
  } = props

  const isEmptyOrContainsUndefined =
    userSetups.length === 0 || userSetups.some(x => x === undefined)

  // Only one setup to pick from, so there is nothing to pick: show it as text.
  const isSingleSetup = userSetups.length === 1

  const toggleOpen = () => {
    setOpen(!open)
  }

  const handleCreateReport = async () => {
    if (reportLoading || dateError || pageError || !selectedDate) return
    dispatch(setReportLoading(true))
    try {
      const response = await Report.getReport(selectedSetup, selectedDate)
      if (response.status === 200) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' }),
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `report_${selectedSetup}_${moment(selectedDate).format('YYYY-MM-DD')}.pdf`,
        )
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        dispatch(changeStatus(STATUS.SUCCESS, 'Report downloaded successfully'))
      }
    } catch (error) {
      console.error(error)
      dispatch(changeStatus(STATUS.ERROR, 'Failed to generate report'))
    } finally {
      dispatch(setReportLoading(false))
    }
  }

  const renderDateSection = () => (
    <Grid item md={12} className={classes.sectionDelimiter}>
      <Typography
        variant="h4"
        className={clsx(classes.formSubtitle, classes.customSubtitleDate)}
      >
        Date selection
      </Typography>
      <DatePicker
        disabled={reportLoading}
        // label="Date"
        value={selectedDate}
        maxDate={moment().toDate()}
        onChange={date => {
          setSelectedDate(date ? moment(date).toDate() : null)
          setDateError(!date)
        }}
        onError={error => setDateError(!!error)}
        inputFormat="YYYY-MM-DD"
        components={{
          OpenPickerIcon: () => (
            <FontAwesomeIcon
              icon={calendarIcon}
              className={classes.endAdornmentIcon}
            />
          ),
        }}
        PopperProps={{
          placement: 'bottom-start',
        }}
        renderInput={params => (
          <CustomTextField
            {...params}
            variant="outlined"
            margin="dense"
            fullWidth
            InputProps={{
              ...params.InputProps,
              className: classes.calendarField,
              endAdornment: params.InputProps.endAdornment,
            }}
          />
        )}
      />
    </Grid>
  )

  const renderCreateReportBtn = () => (
    <Grid item md={12}>
      {reportLoading && (
        <Typography className={classes.generatingText}>
          Generating report ...
        </Typography>
      )}
      <div className={classes.runButtonDelimiter}>
        <PrimaryButton
          onClick={handleCreateReport}
          style={{
            background: (reportLoading || dateError || pageError) && '#ccc',
            width: '100%',
          }}
          disabled={reportLoading || dateError || !!pageError}
        >
          Create Report
        </PrimaryButton>
      </div>
    </Grid>
  )

  return (
    <>
      <Drawer
        open={true}
        variant={'permanent'}
        anchor={'left'}
        ModalProps={{
          keepMounted: true,
          container: container.current,
          style: { position: 'absolute' },
        }}
        PaperProps={{
          style: { position: 'absolute' },
          className: classes.drawerPaper,
        }}
        BackdropProps={{ style: { position: 'absolute' } }}
        onClose={toggleOpen}
        classes={{ paper: classes.drawer }}
      >
        <div className={classes.formContainer}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                className={clsx(classes.formSubtitle, classes.customSubtitle)}
              >
                Setup selection
              </Typography>
              {!isEmptyOrContainsUndefined ? (
                <Grid item md={12}>
                  {isSingleSetup ? (
                    <CollapsedField label="Setup Name" value={userSetups[0]} />
                  ) : (
                    <>
                      <div className={classes.tooltipContainer}>
                        <CustomInputLabel>Setup Name</CustomInputLabel>
                        <Tooltip
                          title="You can search for a value by typing in the box!"
                          placement="top"
                          arrow
                          classes={{
                            tooltip: classes.tooltip,
                            arrow: classes.arrow,
                          }}
                        >
                          <span>
                            <IconButton
                              style={{ padding: '0' }}
                              aria-label="info"
                              disableTouchRipple
                            >
                              <HelpIcon
                                fontSize="small"
                                className={classes.helpIcon}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          disabled={loading || reportLoading}
                          variant="outlined"
                          size="small"
                          options={userSetups}
                          value={selectedSetup}
                          autoHighlight
                          onChange={(event, value) => {
                            setSetup(value)
                          }}
                          renderInput={params => (
                            <CustomTextField
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                                style: { padding: '1px 5px' },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </>
                  )}
                </Grid>
              ) : (
                <CustomInputLabel>
                  No available setup for selection or error occurred.
                </CustomInputLabel>
              )}
            </Grid>
            {selectedSetup && isAdmin && renderDateSection()}
            {selectedSetup && isAdmin && renderCreateReportBtn()}
          </Grid>
        </div>
      </Drawer>
      <div className={classes.childrenContainer}>{props.children}</div>
    </>
  )
}

export default PortfolioOverviewPanel
